import React, { useEffect, useState } from "react";
import { Check, BadgeCheck, Phone, Mail, X, ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import Icon from "./Icon";
import { contact } from "../mock";
import { useLang } from "../i18n";

export function Amenities() {
  const { t } = useLang();
  const s = t.amenitiesSection;
  return (
    <section id="ausstattung" className="surface-soft py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-2xl">
          <p data-reveal className="eyebrow text-[#55575e]">
            {s.eyebrow}
          </p>
          <h2
            data-reveal
            data-delay="1"
            className="mt-4 font-display text-4xl leading-tight text-[#2a2b2f] sm:text-5xl"
          >
            {s.title}
          </h2>
          <p data-reveal data-delay="2" className="mt-5 text-[16px] leading-relaxed text-slate-600">
            {s.lead}
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:auto-rows-fr sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2">
          {t.amenities.map((a, i) => (
            <div
              key={a.title}
              data-reveal
              data-delay={String((i % 4) + 1)}
              className="card-lift flex h-full flex-col rounded-2xl border border-slate-200/80 bg-white p-7"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#2a2b2f] text-white">
                  <Icon name={a.icon} className="h-5 w-5" />
                </span>
                {a.free && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f1f2f4] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#55575e]">
                    <BadgeCheck className="h-3.5 w-3.5" /> {s.freeBadge}
                  </span>
                )}
              </div>
              <h3 className="mt-5 text-[17px] font-semibold text-[#2a2b2f]">{a.title}</h3>
              <ul className="mt-4 space-y-2.5">
                {a.items.map((it) => (
                  <li key={it} className="flex gap-2.5 text-[14px] leading-relaxed text-slate-600">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#55575e]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Prices() {
  const { t } = useLang();
  const p = t.prices;
  return (
    <section id="preise" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p data-reveal className="eyebrow text-[#55575e]">
              {p.eyebrow}
            </p>
            <h2
              data-reveal
              data-delay="1"
              className="mt-4 font-display text-4xl leading-tight text-[#2a2b2f] sm:text-5xl"
            >
              {p.title}
            </h2>
            <p data-reveal data-delay="2" className="mt-5 text-[16px] leading-relaxed text-slate-600">
              {p.lead}
            </p>
            <ul data-reveal data-delay="3" className="mt-8 space-y-3">
              {p.includes.map((it) => (
                <li key={it} className="flex gap-3 text-[15px] text-slate-600">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#55575e]" /> {it}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div
              data-reveal
              data-delay="2"
              className="relative overflow-hidden rounded-[1.5rem] border border-slate-200 bg-[#f5f6f8] p-8 sm:p-12"
            >
              <div
                className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#e4e5e9]"
                aria-hidden="true"
              />
              <div className="relative">
                <p className="eyebrow text-[#55575e]">{p.cardEyebrow}</p>
                <div className="mt-4 flex flex-wrap items-end gap-3">
                  <span className="font-display text-6xl leading-none text-[#2a2b2f] sm:text-7xl">
                    {p.amount}
                  </span>
                  <span className="pb-2 text-[15px] text-slate-500">{p.per}</span>
                </div>
                <p className="mt-4 text-[16px] text-slate-600">{p.sub}</p>
                <div className="mt-8 h-px bg-slate-200" />
                <p className="mt-6 text-[14px] leading-relaxed text-slate-500">{p.note}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={`tel:${contact.phoneLink}`}
                    className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
                  >
                    <Phone className="h-4 w-4" /> {p.ctaCall}
                  </a>
                  <a
                    href={`mailto:${contact.email}`}
                    className="btn-outline inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
                  >
                    <Mail className="h-4 w-4" /> {p.ctaMail}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Gallery() {
  const { t } = useLang();
  const s = t.gallerySection;
  const [tag, setTag] = useState("all");
  const [index, setIndex] = useState(null);

  // Alle Galeriebilder sofort in den Browser-Cache laden,
  // damit beim Filtern und in der Lightbox nichts nachgeladen werden muss
  useEffect(() => {
    t.gallery.forEach((g) => {
      const img = new Image();
      img.decoding = "async";
      img.src = g.src;
    });
  }, [t.gallery]);

  const tagKeys = ["all", "haus", "innen", "zimmer", "umgebung"];
  const items = tag === "all" ? t.gallery : t.gallery.filter((g) => g.tag === tag);

  const close = () => setIndex(null);
  const prev = () => setIndex((i) => (i - 1 + items.length) % items.length);
  const next = () => setIndex((i) => (i + 1) % items.length);

  return (
    <section id="bilder" className="surface-soft py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-xl">
            <p data-reveal className="eyebrow text-[#55575e]">
              {s.eyebrow}
            </p>
            <h2
              data-reveal
              data-delay="1"
              className="mt-4 font-display text-4xl leading-tight text-[#2a2b2f] sm:text-5xl"
            >
              {s.title}
            </h2>
            <p data-reveal data-delay="2" className="mt-5 text-[16px] leading-relaxed text-slate-600">
              {s.lead}
            </p>
          </div>

          <div data-reveal data-delay="3" className="flex flex-wrap gap-2">
            {tagKeys.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setTag(k);
                  setIndex(null);
                }}
                className={`rounded-full px-4 py-2 text-[13px] font-medium transition-colors duration-300 ${
                  tag === k
                    ? "bg-[#2a2b2f] text-white"
                    : "border border-slate-200 bg-white text-slate-600 hover:border-[#b6b8be] hover:text-[#2a2b2f]"
                }`}
              >
                {s.tags[k]}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((g, i) => (
            <button
              key={g.src}
              type="button"
              onClick={() => setIndex(i)}
              data-reveal
              data-delay={String((i % 3) + 1)}
              className="img-zoom group relative overflow-hidden rounded-2xl border border-slate-200 bg-white text-left"
            >
              <img
                src={g.src}
                alt={g.alt}
                loading="eager"
                decoding="async"
                className="pixel-soft h-60 w-full object-cover sm:h-64"
              />
              <span className="absolute left-4 top-4 rounded-full bg-[#1c1d20]/85 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
                {s.tags[g.tag]}
              </span>
              <span className="absolute inset-x-0 bottom-0 translate-y-2 bg-gradient-to-t from-[#1c1d20]/88 to-transparent px-4 pb-4 pt-10 text-[13.5px] font-medium text-white opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                {g.caption}
              </span>
              <span className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-white/92 text-[#2a2b2f] opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <ImageIcon className="h-4 w-4" />
              </span>
            </button>
          ))}
        </div>
      </div>

      {index !== null && items[index] && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-[#1c1d20]/92 p-4 backdrop-blur-sm">
          <button
            type="button"
            aria-label={t.lightbox.close}
            onClick={close}
            className="absolute right-5 top-5 grid h-11 w-11 place-items-center rounded-full border border-white/25 text-white transition-colors hover:bg-white/15"
          >
            <X className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label={t.lightbox.prev}
            onClick={prev}
            className="absolute left-3 sm:left-8 grid h-12 w-12 place-items-center rounded-full border border-white/25 text-white transition-colors hover:bg-white/15"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            aria-label={t.lightbox.next}
            onClick={next}
            className="absolute right-3 sm:right-8 grid h-12 w-12 place-items-center rounded-full border border-white/25 text-white transition-colors hover:bg-white/15"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          <figure className="lightbox-enter w-full max-w-4xl">
            <img
              src={items[index].src}
              alt={items[index].alt}
              className="max-h-[74vh] w-full rounded-2xl object-contain"
            />
            <figcaption className="mt-4 text-center text-[14px] text-white/80">
              {items[index].caption} — {index + 1} / {items.length}
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}
